/**
 * Reusable social auth buttons (Apple + Google).
 * Functional on all platforms.
 */

import { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { signInWithApple, signInWithGoogle } from '@/lib/socialAuth';

export default function SocialAuthButtons() {
  const { theme } = useTheme();
  const c = theme.colors;
  const [loading, setLoading] = useState<'apple' | 'google' | null>(null);
  const [error, setError] = useState('');

  // Apple Sign-In not available on Android natively
  const showApple = Platform.OS !== 'android';

  const handle = async (provider: 'apple' | 'google') => {
    setLoading(provider);
    setError('');
    try {
      if (provider === 'apple') await signInWithApple();
      else await signInWithGoogle();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Sign-in failed';
      setError(message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <View style={styles.wrapper}>
      {/* Divider */}
      <View style={styles.dividerRow}>
        <View style={[styles.dividerLine, { backgroundColor: c.border }]} />
        <Text style={[styles.dividerText, { color: c.textTertiary }]}>or</Text>
        <View style={[styles.dividerLine, { backgroundColor: c.border }]} />
      </View>

      {error ? (
        <Text style={[styles.errorText, { color: c.error ?? c.accent }]}>{error}</Text>
      ) : null}

      {/* Apple */}
      {showApple && (
        <TouchableOpacity
          style={[styles.socialBtn, styles.appleBtn]}
          onPress={() => handle('apple')}
          disabled={loading !== null}
          accessibilityRole="button"
          accessibilityLabel="Continue with Apple">
          {loading === 'apple' ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Text style={styles.appleIcon}>{Platform.OS === 'ios' ? '\uF8FF' : ''}</Text>
              <Text style={styles.appleBtnText}>Continue with Apple</Text>
            </>
          )}
        </TouchableOpacity>
      )}

      {/* Google */}
      <TouchableOpacity
        style={[styles.socialBtn, styles.googleBtn, { borderColor: c.border }]}
        onPress={() => handle('google')}
        disabled={loading !== null}
        accessibilityRole="button"
        accessibilityLabel="Continue with Google">
        {loading === 'google' ? (
          <ActivityIndicator color="#333" />
        ) : (
          <>
            <Text style={styles.googleIcon}>G</Text>
            <Text style={styles.googleBtnText}>Continue with Google</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: { gap: 12, marginTop: 4 },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 4,
  },
  dividerLine: { flex: 1, height: 1 },
  dividerText: { fontSize: 13, fontWeight: '500' },
  errorText: { fontSize: 13, textAlign: 'center' },
  socialBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 2,
    gap: 10,
  },
  appleBtn: { backgroundColor: '#000' },
  appleIcon: {
    fontSize: 18,
    color: '#fff',
  },
  appleBtnText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  googleBtn: {
    backgroundColor: '#fff',
    borderWidth: 1,
  },
  googleIcon: {
    fontSize: 18,
    fontWeight: '700',
    color: '#4285F4',
  },
  googleBtnText: { color: '#333', fontSize: 16, fontWeight: '600' },
});
